import React, { useContext } from "react"
import { Image } from "@chakra-ui/react"
import { VideoButton } from "../../common/buttons/video-button"
import { useRecoilValue, useSetRecoilState, useRecoilState } from "recoil"
import { SDKContext } from "../../../context/SDKContext"
import { brandingSelector } from "../../../recoil/atoms/room"
import {
  Container,
  Wrapper,
  ImageContainer,
  InfoContainer,
  Title,
  Description
} from "../badges/badge.styled"
import { checkIsModNotPresentId, cleanProductDescription, stripHtmlTags } from "../../../util"
import {
  shoppableVideosSelector,
  moderatorRequiredShoppableVideosSelector
} from "../../../recoil/selectors/shoppable-videos-selector"
import { playerPropsSelector } from "../../../recoil/atoms/player"
import { localUserCallbackSelector, localUserIdAtom } from "../../../recoil/atoms/users"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import { pipParamsSelector } from "../../../recoil/atoms/pictureInPicture"
import { ShoppableVideo } from "../../../recoil/atoms/shoppableVideos/types"
import { taggedBadgesAtom } from "../../../recoil/atoms/taggedBadges"
import {
  shoppableMNPVideoAtom,
  shoppableVideosAfterPictureNoPresentAtom
} from "../../../recoil/atoms/shoppableVideos"
import { useTranslation } from "react-i18next"
import { usePictureInPicture } from "../../../hooks/usePictureInPicture"
import { useWatchParty } from "../../../hooks/useWatchParty"
import { watchPartyHostIdSelector } from "../../../recoil/atoms/watchPartyScreen"
import { waitForElm } from "../../../util/waitForElm"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import { showNoVideoSubscribeScreenSelector } from "../../../recoil/atoms/rtc"

interface VideoBadgeProps extends ShoppableVideo {
  isMobile?: boolean
  isTransparent?: boolean
  isSomeHaveOnDevice: boolean
  isLast: boolean
}

const MeetOnline = window.MeetOnline

const VideoBadge: React.FC<VideoBadgeProps> = React.memo(
  ({
    isMobile,
    description,
    title,
    thumbnailUrl,
    shoppableEnabled,
    watchPartyEnabled,
    mediaUrl,
    products,
    id,
    isSomeHaveOnDevice,
    isLast
  }) => {
    const { t } = useTranslation()
    const roomStyles = useRecoilValue(brandingSelector)
    const { isModerator } = useLocalUserType()
    const { isLandscape, isTransparent } = useRecoilValue(
      viewDependingLayoutAtom
    )
    const {
      watchPartyHostId,
      isWatchParty,
      isModPresentImage,
      isModPresentVideo,
      isHost
    } = useWatchParty()
    const setPlayerProps = useSetRecoilState(playerPropsSelector)
    const isShoppableVideos = useRecoilValue(shoppableVideosSelector)
    const [
      { enabledShoppableMNP, idShoppableMNP, typeShoppableMNP },
      setShoppableMNPVideo
    ] = useRecoilState(shoppableMNPVideoAtom)
    const [
      {
        isShoppableVideosAfterPictureNoPresent,
        shoppableVideosAfterPictureNoPresentHostId
      },
      setShoppableVideosAfterPictureNoPresent
    ] = useRecoilState(shoppableVideosAfterPictureNoPresentAtom)
    const { exitPictureInPicture } = usePictureInPicture()
    const { isActivePiP } = useRecoilValue(pipParamsSelector)
    const setPiPParams = useSetRecoilState(pipParamsSelector)
    const setTaggedBadges = useSetRecoilState(taggedBadgesAtom)
    const _localUserId = useRecoilValue(localUserIdAtom)
    const setWatchPartyHostId = useSetRecoilState(watchPartyHostIdSelector)
    const isMediaDisabled = useRecoilValue(showNoVideoSubscribeScreenSelector)
    const isModeratorRequiredShoppableVideos = useRecoilValue(
      moderatorRequiredShoppableVideosSelector
    )
    const meetingClient = useContext(SDKContext)
    const localUserCallbackProps = useRecoilValue(localUserCallbackSelector)

    const isShareVideoEnabled = React.useMemo<boolean>(() => {
      return (
        enabledShoppableMNP &&
        idShoppableMNP === id &&
        typeShoppableMNP === "share"
      )
    }, [enabledShoppableMNP, id, idShoppableMNP, typeShoppableMNP])

    const isPlayVideoEnabled = React.useMemo<boolean>(() => {
      return (
        enabledShoppableMNP &&
        // eslint-disable-next-line eqeqeq
        idShoppableMNP == id &&
        typeShoppableMNP === "play"
      )
    }, [enabledShoppableMNP, id, idShoppableMNP, typeShoppableMNP])

    const isWatchPartyActiveExceptMNP = isWatchParty && !checkIsModNotPresentId(watchPartyHostId);

    // Disable the share button if watch party is active and user is not an host
    const disabledShareButton = (isWatchPartyActiveExceptMNP && !isHost ) || isMediaDisabled

    const isDisablePlayButton = React.useMemo<boolean>(() => {
      if(isMediaDisabled || isWatchPartyActiveExceptMNP) return true
      
      return (
        (!isModerator &&
          isWatchParty &&
          !isModPresentImage &&
          !isModPresentVideo) ||
        isSomeHaveOnDevice
      )
    }, [
      isModPresentImage,
      isModPresentVideo,
      isModerator,
      isSomeHaveOnDevice,
      isWatchParty,
      isMediaDisabled,
      isWatchPartyActiveExceptMNP
    ])


    const handleStopWatchParty = React.useCallback(async () => {
      if (watchPartyHostId === _localUserId) {
        if (isActivePiP) {
          exitPictureInPicture()
          setPiPParams({ isActivePiP: false })
        }

        await meetingClient?.rtc?.stopWatchParty()
        await meetingClient?.sendStoppingWatchParty(_localUserId)
      }
    }, [
      _localUserId,
      exitPictureInPicture,
      isActivePiP,
      meetingClient,
      setPiPParams,
      watchPartyHostId
    ])

    const handleResetShoppableMNPVideo = React.useCallback(() => {
      setShoppableMNPVideo({
        srcShoppableMNP: "",
        enabledShoppableMNP: false,
        idShoppableMNP: "",
        typeShoppableMNP: ""
      })
      setTaggedBadges([])
    }, [setShoppableMNPVideo, setTaggedBadges])

    const playVideo = React.useCallback(
      async e => {
        e.stopPropagation()

        await handleStopWatchParty()
        await handleResetShoppableMNPVideo()
        setPlayerProps({ paused: false })
        setShoppableMNPVideo({
          idShoppableMNP: id,
          srcShoppableMNP: mediaUrl,
          enabledShoppableMNP: true,
          typeShoppableMNP: "play"
        })

        const productsData = await MeetOnline?.client().fetchProductByIds(
          products
        )

        setTaggedBadges(productsData)

        if (isModeratorRequiredShoppableVideos || isModPresentImage) {
          if (isModPresentImage) {
            setShoppableVideosAfterPictureNoPresent({
              isShoppableVideosAfterPictureNoPresent: true,
              shoppableVideosAfterPictureNoPresentHostId: watchPartyHostId
            })
          }
          setWatchPartyHostId("modNotPresentVideo")
        }
      },
      [
        handleResetShoppableMNPVideo,
        handleStopWatchParty,
        id,
        isModPresentImage,
        isModeratorRequiredShoppableVideos,
        mediaUrl,
        products,
        setPlayerProps,
        setShoppableMNPVideo,
        setShoppableVideosAfterPictureNoPresent,
        setTaggedBadges,
        setWatchPartyHostId,
        watchPartyHostId
      ]
    )

    const stopVideo = React.useCallback(
      e => {
        e.stopPropagation()
        

        setPlayerProps({ paused: true })
        if (
          isModeratorRequiredShoppableVideos ||
          isShoppableVideosAfterPictureNoPresent
        ) {
          if (isShoppableVideosAfterPictureNoPresent) {
            setWatchPartyHostId(shoppableVideosAfterPictureNoPresentHostId)
            setShoppableVideosAfterPictureNoPresent({
              isShoppableVideosAfterPictureNoPresent: false,
              shoppableVideosAfterPictureNoPresentHostId: ""
            })
          } else {
            setWatchPartyHostId("")
          }
        }
        handleResetShoppableMNPVideo()
      },
      [
        handleResetShoppableMNPVideo,
        isModeratorRequiredShoppableVideos,
        isShoppableVideosAfterPictureNoPresent,
        setPlayerProps,
        setShoppableVideosAfterPictureNoPresent,
        setWatchPartyHostId,
        shoppableVideosAfterPictureNoPresentHostId
      ]
    )

    const handleShareVideo = React.useCallback(
      async e => {
        e.stopPropagation()
        if (isWatchParty && !isModPresentImage) {
          const video = (await waitForElm(
            "watchpartyvideoplayer"
          )) as HTMLVideoElement
          

          setPlayerProps({ paused: true })
          await video.pause()

          await meetingClient?.rtc?.stopWatchParty()
          await meetingClient?.sendStoppingWatchParty(_localUserId)
        }

        let timer = setTimeout(() => {
          

          setPlayerProps({ paused: true })
          meetingClient?.rtc?.setMediaProductProps({
            id: id,
            productsIds: products,
            enabled: true
          })
          meetingClient?.rtc?.toggleWatchParty(mediaUrl)
          meetingClient?.sendStartingWatchParty(_localUserId)
          setShoppableMNPVideo({
            srcShoppableMNP: "",
            enabledShoppableMNP: true,
            idShoppableMNP: id,

            typeShoppableMNP: "share"
          })
        }, 1000)
        const productsData = await MeetOnline?.client().fetchProductByIds(
          products
        )
        setTaggedBadges(productsData)

        return () => clearTimeout(timer)
      },
      [
        _localUserId,
        id,
        isModPresentImage,
        isWatchParty,
        mediaUrl,
        meetingClient,
        products,
        setPlayerProps,
        setShoppableMNPVideo,
        setTaggedBadges
      ]
    )

    const handleStopShareVideo = React.useCallback(
      e => {
        e.stopPropagation()
        handleStopWatchParty()
        handleResetShoppableMNPVideo()
      },
      [handleResetShoppableMNPVideo, handleStopWatchParty]
    )

    const handleToggleShareVideo = React.useCallback(
      async e => {
        const localUser = await localUserCallbackProps.handleReturnData()
        // If stop screenshare if enabled
        if (localUser?.screenShareEnabled) {
          await MeetOnline?.client().rtc.toggleScreenShare(false)
        }
        return isShareVideoEnabled ? handleStopShareVideo(e) : handleShareVideo(e)
      },
      [handleShareVideo, handleStopShareVideo, isShareVideoEnabled, localUserCallbackProps]
    )

    const handleTogglePlayVideo = React.useCallback(
      e => (isPlayVideoEnabled ? stopVideo(e) : playVideo(e)),
      [isPlayVideoEnabled, playVideo, stopVideo]
    )


    return (
      <Container
        key={`key-badge-${id}`}
        roomStyles={roomStyles}
        isTransparent={isTransparent || isLandscape}
        style={{
          opacity: 1,
          marginBottom: isLast ? "0px" : "10px",
          padding: "0.25rem",
          borderWidth: isMobile && "1px"
        }}
      >
        <Wrapper>
          <ImageContainer>
            <Image
              maxW="max-content"
              src={thumbnailUrl}
              alt={title}
              maxHeight="65px"
              maxWidth="65px"
              objectFit="contain"
              opacity={1}
              borderRadius="5px"
            />
          </ImageContainer>

          <InfoContainer style={{ paddingRight: isMobile && "0rem" }} isImage={true}>
            <Title
              isTransparent={isTransparent}
              roomStyles={roomStyles}
              isMobile={isMobile}
              style={{ fontSize: isMobile && "1rem" }}
            >
              {title}
            </Title>
            <Description
              isTransparent={isTransparent}
              roomStyles={roomStyles}
              style={{
                marginBottom: isMobile && "0.22rem",
                fontSize: isMobile && "1rem"
              }}
            >
              {cleanProductDescription(stripHtmlTags(description))}
            </Description>
            <Wrapper
              style={{
                marginLeft: "auto",
                width: "fit-content",
                justifyContent: "unset"
              }}
            >
              {isModerator && watchPartyEnabled && (
                <VideoButton
                  onClick={handleToggleShareVideo}
                  onTouchEnd={e => e.stopPropagation()}
                  isMobile={isMobile}
                  text={isShareVideoEnabled && isHost ? t("Stop Sharing") : t("Share")}
                  isActive={isShareVideoEnabled && isHost}
                  isDisabled={disabledShareButton}
                />
              )}
              {isShoppableVideos && shoppableEnabled && (
                <VideoButton
                  onClick={handleTogglePlayVideo}
                  onTouchEnd={e => e.stopPropagation()}
                  marginLeft="0.5rem"
                  isDisabled={isDisablePlayButton}
                  isMobile={isMobile}
                  isActive={isPlayVideoEnabled}
                  text={isPlayVideoEnabled ? t("Stop") : t("Play")}
                />
              )}
            </Wrapper>
          </InfoContainer>
        </Wrapper>
      </Container>
    )
  }
)

export default VideoBadge
